<template>
  <v-card class="my-4">
    <v-card-title>
      Ad segments
      <v-spacer />
      <v-btn
        small
        outlined
        class="mr-2"
        :loading="testing"
        @click="testConnection"
      >
        <v-icon
          left
          small
        >
          mdi-lan-connect
        </v-icon>
        Test Google connection
      </v-btn>
      <v-btn
        icon
        title="Recount"
        :loading="loading"
        @click="load"
      >
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>

    <!-- Credential state sits above the segments because none of them can sync without it, and a
         failed connection is the single most likely reason an upload does nothing. -->
    <v-card-text
      v-if="connection"
      class="pb-0"
    >
      <v-alert
        dense
        text
        :type="connection.success ? 'success' : 'error'"
        class="mb-0"
      >
        {{ connection.message }}
      </v-alert>
    </v-card-text>

    <v-card-subtitle>
      Who we can upload to Google as a Customer Match audience. Counts are computed live from current
      accounts — never cached, so a segment can't drift out of date with the people it describes.
      Only enabled segments are ever uploaded.
    </v-card-subtitle>

    <v-card-text v-if="firstLoad">
      <v-skeleton-loader type="table-tbody" />
    </v-card-text>

    <v-card-text v-else-if="error">
      <v-alert
        type="error"
        text
        dense
        class="mb-0"
      >
        {{ error }}
      </v-alert>
    </v-card-text>

    <template v-else>
      <v-simple-table dense>
        <template #default>
          <thead>
            <tr>
              <th>Segment</th>
              <th class="text-right">
                Members
              </th>
              <th>Status</th>
              <th>Last sync</th>
              <th class="text-right">
                Enabled
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="segment in segments"
              :key="segment.key"
            >
              <td class="py-2">
                <div class="font-weight-medium">
                  {{ segment.name }}
                  <v-chip
                    v-if="segment.isSuppression"
                    x-small
                    color="grey lighten-2"
                    class="ml-1"
                  >
                    exclusion
                  </v-chip>
                </div>
                <div class="caption grey--text">
                  {{ segment.description }}
                </div>
              </td>

              <td class="text-right font-weight-medium">
                {{ segment.memberCount.toLocaleString() }}
              </td>

              <td>
                <!-- Suppression lists are exclusions, so serving minimums don't apply to them —
                     flagging one as "too small" would be actively misleading. -->
                <span
                  v-if="segment.isSuppression"
                  class="caption grey--text"
                >—</span>
                <v-tooltip
                  v-else
                  bottom
                  max-width="320"
                >
                  <template #activator="{ on }">
                    <v-chip
                      x-small
                      :color="statusFor(segment).color"
                      text-color="white"
                      v-on="on"
                    >
                      {{ statusFor(segment).label }}
                    </v-chip>
                  </template>
                  <span>{{ statusFor(segment).hint }}</span>
                </v-tooltip>
              </td>

              <td class="caption">
                <span v-if="!segment.lastSyncDate" class="grey--text">Never</span>
                <span v-else>
                  <v-icon
                    x-small
                    :color="segment.lastSyncSuccess ? 'success' : 'error'"
                  >
                    {{ segment.lastSyncSuccess ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                  </v-icon>
                  {{ segment.lastSyncDate | normalDateWithTime }}
                </span>
              </td>

              <td class="text-right">
                <!-- Only offered for enabled segments: syncing one that's switched off would push
                     an audience nobody chose to build. -->
                <v-btn
                  v-if="segment.enabled"
                  x-small
                  outlined
                  class="mr-2"
                  :loading="syncing === segment.key && !syncDryRun"
                  :disabled="syncing !== null"
                  @click="confirmSync = segment"
                >
                  Sync now
                </v-btn>
                <v-btn
                  v-if="segment.enabled"
                  x-small
                  text
                  class="mr-2"
                  :loading="syncing === segment.key && syncDryRun"
                  :disabled="syncing !== null"
                  @click="syncSegment(segment, true)"
                >
                  Dry run
                </v-btn>
                <v-switch
                  :input-value="segment.enabled"
                  :loading="saving === segment.key"
                  :disabled="saving !== null"
                  dense
                  hide-details
                  class="mt-0 pt-0 d-inline-flex"
                  @change="setEnabled(segment, $event)"
                />
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <v-card-text class="caption grey--text">
        Turning a segment on marks it for the nightly sync. "Dry run" resolves and hashes the
        audience without sending anything — use it to check how many members survive hashing.
      </v-card-text>
    </template>

    <v-dialog
      :value="!!confirmSync"
      max-width="480"
      @input="confirmSync = null"
    >
      <v-card v-if="confirmSync">
        <v-card-title>Upload to Google?</v-card-title>
        <v-card-text>
          This sends {{ confirmSync.memberCount.toLocaleString() }} hashed customer email addresses
          to Google Ads and creates a real audience in
          <strong>{{ confirmSync.name }}</strong>. Addresses are SHA-256 hashed before they leave
          the server, but this is real customer data going to a third party — and once a list
          exists, removing it is a manual step in Audience manager.
          <div class="mt-2">
            Use <strong>Dry run</strong> instead if you only want to check the numbers.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="confirmSync = null"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="syncSegment(confirmSync, false)"
          >
            Upload
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: 'RetargetingSegments',

  data() {
    return {
      loading: false,
      loaded: false,
      saving: null,
      syncing: null,
      syncDryRun: false,
      // Segment awaiting confirmation for a REAL upload. A click that sends customer email
      // addresses to Google shouldn't be one gesture away.
      confirmSync: null,
      testing: false,
      connection: null,
      error: null,
      segments: []
    };
  },

  computed: {
    firstLoad() {
      return this.loading && !this.loaded;
    }
  },

  created() {
    this.load();
  },

  methods: {
    // Three states rather than servable/not, because Display and Search have different bars and
    // Display is where dynamic remarketing actually runs. Calling a 432-member segment "below
    // minimum" would write off a list that works fine for the campaigns we're building first.
    statusFor(segment) {
      if (!segment.servesDisplay) {
        return {
          label: 'too small',
          color: 'error',
          hint:
            'Under ~100 matched members, so it cannot serve anywhere. Combine it with another segment or grow it before building a campaign on it.'
        };
      }

      if (!segment.servesAllSurfaces) {
        return {
          label: 'display only',
          color: 'warning',
          hint:
            'Clears the ~100 needed for Display — which is where dynamic remarketing runs — but not the ~1,000 Search, YouTube and Gmail require. Usable now for the feed-driven campaigns.'
        };
      }

      return {
        label: segment.marginalAfterMatchRate ? 'servable (marginal)' : 'servable',
        color: segment.marginalAfterMatchRate ? 'warning' : 'success',
        hint: segment.marginalAfterMatchRate
          ? 'Clears every surface on raw count, but thresholds are measured against MATCHED members and match rates run well under 100% — this one could drop below once Google finishes matching.'
          : 'Comfortably clears Display, Search, YouTube and Gmail even after match-rate losses.'
      };
    },

    testConnection() {
      this.testing = true;
      this.$http
        .get('retargeting/testGoogleAdsConnection')
        .then((response) => {
          this.connection = response.data;
        })
        .catch((error) => {
          // The endpoint returns a structured failure for credential problems, so reaching here
          // means the request itself broke — worth distinguishing from "Google said no".
          this.connection = {
            success: false,
            message: error.message || 'Could not reach the connection test.'
          };
        })
        .finally(() => {
          this.testing = false;
        });
    },

    syncSegment(segment, dryRun) {
      this.confirmSync = null;
      this.syncing = segment.key;
      this.syncDryRun = !!dryRun;
      this.$http
        .post('retargeting/syncSegment', { segmentKey: segment.key, dryRun: !!dryRun })
        .then((response) => {
          const data = response.data || {};

          if (!data.success) {
            this.$root.$alert('Sync failed', data.errorMessage || 'Unknown error.');
          } else if (data.dryRun) {
            // Never say "sent" for a dry run. Whether customer email addresses left the building
            // is the one thing this dialog must not be vague about.
            this.$root.$alert(
              'Nothing uploaded',
              `${data.forcedDryRunReason || 'Dry run'} — ${data.hashableCount.toLocaleString()} of ${data.memberCount.toLocaleString()} members resolved to a usable hashed email.` +
                (data.unhashableCount ? ` ${data.unhashableCount.toLocaleString()} had no usable address and would be dropped.` : '')
            );
          } else {
            this.$root.$alert(
              'Uploaded',
              `${data.memberCount.toLocaleString()} members sent to Google. Matching happens on their side — the match rate appears in the Ads UI within a few hours, not here.`
            );
          }
          // Reload either way: the sync log row is written on success AND failure, and the "Last
          // sync" column is the only place a failed run is visible after the dialog closes.
          this.load();
        })
        .catch((error) => {
          this.$root.$alert('Error', error.message || 'Could not sync the segment.');
        })
        .finally(() => {
          this.syncing = null;
        });
    },

    load() {
      this.loading = true;
      this.error = null;
      this.$http
        .get('retargeting/segments')
        .then((response) => {
          this.segments = response.data || [];
          this.loaded = true;
        })
        .catch((error) => {
          this.error = error.message || 'Could not load segments.';
        })
        .finally(() => {
          this.loading = false;
        });
    },

    setEnabled(segment, value) {
      // Vuetify hands back null when a switch is cleared rather than toggled; coerce so we never
      // post null and silently disable something the operator meant to enable.
      const enabled = !!value;
      this.saving = segment.key;

      this.$http
        .post('retargeting/setSegmentEnabled', { segmentKey: segment.key, enabled })
        .then(() => {
          segment.enabled = enabled;
        })
        .catch((error) => {
          this.$root.$alert('Error', error.message || 'Could not update the segment.');
        })
        .finally(() => {
          this.saving = null;
        });
    }
  }
};
</script>
